import 'src/styles/post-processor-queue.css';

import { faAlignLeft, faChevronDown, faCube, faInfoCircle, faMapMarkerAlt, faPlay, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import MaintenanceHealthBadge from 'src/components/maintenance/MaintenanceHealthBadge';
import FeaturesContext from 'src/context/FeaturesContext';
import { api } from 'src/utils/api';
import {
  API_RESOURCES,
  PRIORITIES,
  PRIORITIES_MAP,
  RUN_STATUS,
  RUN_STATUS_LABELS,
} from 'src/utils/constants';
import { FEATURES, isFeatureEnabled } from 'src/utils/features';
import routes from 'src/utils/routes';
import { getRouteURI, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

const RUNS_PER_PAGE = 5;

const getPriorityBadgeClass = (priorityValue) => {
  switch (priorityValue) {
    case PRIORITIES.CRITICAL:
      return 'ppq-priority-badge--critical';
    case PRIORITIES.HIGH:
      return 'ppq-priority-badge--high';
    case PRIORITIES.LOW:
      return 'ppq-priority-badge--low';
    default:
      return 'ppq-priority-badge--normal';
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case RUN_STATUS.queuedReady:
      return 'ppq-status-badge--queued-ready';
    case RUN_STATUS.queued:
      return 'ppq-status-badge--queued';
    case RUN_STATUS.inProgress:
      return 'ppq-status-badge--in-progress';
    case RUN_STATUS.paused:
      return 'ppq-status-badge--paused';
    default:
      return 'ppq-status-badge--default';
  }
};

const PostProcessorQueuePage = ({ user }) => {
  const { uuid } = useParams();
  const { addToast } = useToasts();
  const { features } = useContext(FeaturesContext);
  const isAiScadaIntegration = isFeatureEnabled(features, FEATURES.AI_SCADA_INTEGRATION);
  const isMaintenanceEnabled = isFeatureEnabled(features, FEATURES.EQUIPMENT_MAINTENANCE);

  const [postProcessor, setPostProcessor] = useState(null);
  const [subLocation, setSubLocation] = useState(null);
  const [runs, setRuns] = useState([]);
  const [activeRuns, setActiveRuns] = useState([]);
  const [scheduleRunsByRun, setScheduleRunsByRun] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [startingRuns, setStartingRuns] = useState(false);
  const [selectedRunUris, setSelectedRunUris] = useState([]);
  const [totalRuns, setTotalRuns] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOrderedLoadCarries, setIsOrderedLoadCarries] = useState(false);

  const isAtMaxCapacity = postProcessor?.in_progress_max > 0 &&
    postProcessor?.in_progress?.length >= postProcessor?.in_progress_max;

  const remainingCapacity = postProcessor?.in_progress_max > 0
    ? Math.max(0, postProcessor.in_progress_max - (postProcessor?.in_progress?.length || 0))
    : Infinity;

  const fetchPostProcessor = useCallback(async () => {
    try {
      const data = await api.get(`${API_RESOURCES.POST_PROCESSOR}/${uuid}/`).json();
      const subLocationResponse = data.sub_location ? await api.get(
        `${API_RESOURCES.SUB_LOCATION}/${getUuid(data.sub_location)}/`
      ).json() : null;
      setPostProcessor(data);
      setSubLocation(subLocationResponse);
    } catch (error) {
      addToast('Failed to fetch post processor information', { appearance: 'error' });
    }
  }, [uuid, addToast]);

  const fetchActiveRuns = useCallback(async () => {
    if (!postProcessor?.uri) return;
    try {
      const activeRunsResponse = await api.get(`${API_RESOURCES.RUN}/`, {
        searchParams: {
          'filter[post_processor]': postProcessor.uri,
          'filter[status]': [RUN_STATUS.inProgress, RUN_STATUS.paused].join(','),
          'sort': 'workstation_queue_position',
        },
      }).json();

      const scheduleRunsResponse = activeRunsResponse.resources?.length ? await api.get(`${API_RESOURCES.SCHEDULE_RUNS}/`, {
        searchParams: {
          'filter[run]': activeRunsResponse.resources.map(r => r.uri).join(','),
        },
      }).json() : { resources: [] };

      setActiveRuns(activeRunsResponse.resources);
      setScheduleRunsByRun((prev) => {
        const newScheduleRunsByRun = { ...prev };
        scheduleRunsResponse.resources.forEach((scheduleRun) => {
          newScheduleRunsByRun[scheduleRun.run] = scheduleRun;
        });
        return newScheduleRunsByRun;
      });
    } catch (error) {
      addToast('Failed to fetch active runs', { appearance: 'error' });
    }
  }, [postProcessor?.uri, addToast]);

  const fetchRuns = useCallback(async (page = 1, append = false) => {
    if (!postProcessor?.uri) return;
    try {
      if (append) setLoadingMore(true);
      const runsResponse = await api.get(`${API_RESOURCES.RUN}/`, {
        searchParams: {
          'filter[post_processor]': postProcessor.uri,
          'filter[status]': [RUN_STATUS.queued, RUN_STATUS.queuedReady].join(','),
          'page[limit]': RUNS_PER_PAGE,
          'page[offset]': (page - 1) * RUNS_PER_PAGE,
          'sort': 'workstation_queue_position',
        },
      }).json();

      const scheduleRunsResponse = runsResponse.resources?.length ? await api.get(`${API_RESOURCES.SCHEDULE_RUNS}/`, {
        searchParams: {
          'filter[run]': runsResponse.resources.map(r => r.uri).join(','),
        },
      }).json() : { resources: [] };

      if (append) {
        setRuns((prev) => [...prev, ...runsResponse.resources]);
      } else {
        setRuns(runsResponse.resources);
      }
      setScheduleRunsByRun((prev) => {
        const newScheduleRunsByRun = { ...prev };
        scheduleRunsResponse.resources.forEach((scheduleRun) => {
          newScheduleRunsByRun[scheduleRun.run] = scheduleRun;
        });
        return newScheduleRunsByRun;
      });
      setTotalRuns(runsResponse.meta.count);
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      addToast('Failed to fetch runs queue', { appearance: 'error' });
      setLoading(false);
      setLoadingMore(false);
    }
  }, [postProcessor?.uri, addToast]);

  useEffect(() => {
    fetchPostProcessor();
    fetchActiveRuns();
    fetchRuns();
  }, [fetchPostProcessor, fetchActiveRuns, fetchRuns]);

  const handleSelectRun = (run) => {
    if (run.status !== RUN_STATUS.queuedReady) return;
    const uri = run.uri;
    setSelectedRunUris((prev) => {
      if (prev.includes(uri)) {
        return prev.filter((u) => u !== uri);
      }
      if (prev.length < remainingCapacity) {
        return [...prev, uri];
      }
      return prev;
    });
  };

  const selectableRuns = runs.filter((r) => r.status === RUN_STATUS.queuedReady);

  const handleSelectAll = () => {
    const maxSelectable = Math.min(selectableRuns.length, remainingCapacity);
    if (selectedRunUris.length >= maxSelectable && selectedRunUris.length > 0) {
      setSelectedRunUris([]);
    } else {
      setSelectedRunUris(selectableRuns.slice(0, remainingCapacity).map((r) => r.uri));
    }
  };

  const handleStartRuns = async () => {
    setStartingRuns(true);
    try {
      await Promise.all(
        selectedRunUris.map((uri) =>
          api.put(uri, {
            prefixUrl: false,
            json: { status: RUN_STATUS.inProgress },
          }),
        ),
      );
      addToast('Runs started successfully', { appearance: 'success' });
      setSelectedRunUris([]);
      setCurrentPage(1);
      fetchPostProcessor(); // Refresh the post processor
      fetchActiveRuns(); // Refresh active runs
      fetchRuns(); // Refresh the list
    } catch (error) {
      addToast('Failed to start some runs', { appearance: 'error' });
    } finally {
      setStartingRuns(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchRuns(nextPage, true);
  };

  const handleOrderLoadCarriers = () => {
    addToast('Your order has been processed', { appearance: 'success' });
    setIsOrderedLoadCarries(true);
  };

  const renderRunRow = (run, index, isQueue = true) => {
    const priority = Object.keys(PRIORITIES).find(
      key => PRIORITIES[key] === run.priority
    ) || 'NORMAL';
    const priorityValue = PRIORITIES[priority];
    const scheduleRun = scheduleRunsByRun[run.uri];
    return (
      <tr key={run.uri}>
        <td>
          {isQueue && (
            <Form.Check
              type='checkbox'
              checked={selectedRunUris.includes(run.uri)}
              className='ppq-select-run'
              disabled={run.status !== RUN_STATUS.queuedReady || (!selectedRunUris.includes(run.uri) && selectedRunUris.length >= remainingCapacity)}
              onChange={() => handleSelectRun(run)}
            />
          )}
        </td>
        <td>{index + 1}</td>
        <td>
          <Link className='ppq-run-link' to={getRouteURI(routes.run, { uuid: getUuid(run.uri) })}>
            {run.name}
          </Link>
        </td>
        <td>
          <span className={`ppq-priority-badge ${getPriorityBadgeClass(priorityValue)}`}>
            {PRIORITIES_MAP[priorityValue]?.defaultMessage || 'Normal'}
          </span>
        </td>
        <td>
          <span className={`ppq-status-badge ${getStatusBadgeClass(run.status)}`}>
            {RUN_STATUS_LABELS[run.status]}
          </span>
        </td>
        <td>
          {scheduleRun?.estimates?.start ?
            dayjs(scheduleRun?.estimates?.start).format('YYYY/MM/DD, HH:mm') : '-'}
        </td>
        <td>
          {scheduleRun?.estimates?.end ?
            dayjs(scheduleRun?.estimates?.end).format('YYYY/MM/DD, HH:mm') : '-'}
        </td>
      </tr>
    );
  };

  const renderMobileCard = (run, index, isQueue = true) => {
    const priority = Object.keys(PRIORITIES).find(
      key => PRIORITIES[key] === run.priority
    ) || 'NORMAL';
    const priorityValue = PRIORITIES[priority];
    const scheduleRun = scheduleRunsByRun[run.uri];
    return (
      <div key={run.uri} className='ppq-mobile-card'>
        <div className='ppq-mobile-card-header'>
          {isQueue && (
            <Form.Check
              type='checkbox'
              checked={selectedRunUris.includes(run.uri)}
              className='ppq-select-run'
              disabled={run.status !== RUN_STATUS.queuedReady || (!selectedRunUris.includes(run.uri) && selectedRunUris.length >= remainingCapacity)}
              onChange={() => handleSelectRun(run)}
            />
          )}
          <span className='ppq-mobile-card-pos' onClick={() => isQueue && handleSelectRun(run)}>
            {`#${index + 1}`}
          </span>
          <span className={`ppq-priority-badge ${getPriorityBadgeClass(priorityValue)}`}>
            {(PRIORITIES_MAP[priorityValue]?.defaultMessage || 'Normal').slice(0, 1)}
          </span>
          <Link className='ppq-run-link ppq-mobile-card-name' to={getRouteURI(routes.run, { uuid: getUuid(run.uri) })}>
            {run.name}
          </Link>
          <span className={`ppq-status-badge ${getStatusBadgeClass(run.status)}`}>
            {RUN_STATUS_LABELS[run.status]}
          </span>
        </div>
        <div className='ppq-mobile-card-row'>
          <span className='ppq-mobile-card-label'>Scheduled start:</span>
          <span>
            {scheduleRun?.estimates?.start ?
              dayjs(scheduleRun?.estimates?.start).format('YYYY/MM/DD, HH:mm') : '-'}
          </span>
        </div>
        <div className='ppq-mobile-card-row'>
          <span className='ppq-mobile-card-label'>Scheduled end:</span>
          <span>
            {scheduleRun?.estimates?.end ?
              dayjs(scheduleRun?.estimates?.end).format('YYYY/MM/DD, HH:mm') : '-'}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Header excludeFromHistory title='Loading' user={user} />
        <main role='main' className='text-center'>
          <Loader />
        </main>
      </>
    );
  }

  return (
    <>
      <Header title='Post processor information' user={user} />
      <div role='main' className='ppq-page'>
        <div className='ppq-container'>
          <p className='ppq-title'>
            <span>
              Post processor name:{' '}
              <span className='ppq-title-name'>{postProcessor?.name}</span>
            </span>
            {isMaintenanceEnabled && (
              <MaintenanceHealthBadge equipmentUri={postProcessor.uri} />
            )}
          </p>

          <div className='ppq-location-card'>
            <div className='ppq-location-info'>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <div>
                <div className='ppq-location-name'>{postProcessor?.location_name}</div>
                <div className='ppq-location-room'>{subLocation?.name}</div>
              </div>
            </div>
            <div className='ppq-availability'>
              <span className='ppq-availability-dot' />
              <span>Available</span>
            </div>
          </div>

          {isMaintenanceEnabled && (
            <Link
              to={getRouteURI(routes.postProcessorMaintenance, { uuid })}
              className='link-btn'
            >
              <button type='button' className='btn btn-lg btn-primary btn-block fs-mobile-18 mb-3'>
                <FontAwesomeIcon icon={faScrewdriverWrench} className='mr-2' />
                Maintenance
              </button>
            </Link>
          )}

          <div className='ppq-queue-card'>
            <div className='ppq-queue-header'>
              <div className='ppq-queue-title'>
                <FontAwesomeIcon icon={faAlignLeft} />
                Run queue
              </div>
              <div className='ppq-queue-count'>
                Showing {runs.length + activeRuns.length} of {totalRuns + activeRuns.length}
              </div>
            </div>
            {activeRuns.length > 0 && (
              <>
                <div className='ppq-subsection-header'>
                  Active runs ({postProcessor?.in_progress.length} of {postProcessor?.in_progress_max} in progress)
                </div>
                <div className='table-responsive ppq-table-wrapper'>
                  <table className='ppq-table'>
                    <thead>
                      <tr>
                        <th />
                        <th>Pos.</th>
                        <th>Run name</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Scheduled start</th>
                        <th>Scheduled end</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeRuns.map((run, index) => renderRunRow(run, index, false))}
                    </tbody>
                  </table>
                </div>
                <div className='ppq-mobile-cards'>
                  {activeRuns.map((run, index) => renderMobileCard(run, index, false))}
                </div>
                <div className='ppq-subsection-header'>Upcoming runs</div>
              </>
            )}
            {isAtMaxCapacity && (
              <div className='ppq-info-banner ppq-info-banner--warning'>
                <FontAwesomeIcon icon={faInfoCircle} className='ppq-info-banner-icon' />
                <span>Processor capacity has been reached. New runs cannot be started at this time. Please wait until capacity becomes available and try again.</span>
              </div>
            )}
            {runs.some((r) => r.status === RUN_STATUS.queued) && (
              <div className='ppq-info-banner'>
                <FontAwesomeIcon icon={faInfoCircle} className='ppq-info-banner-icon' />
                <span>Queued runs cannot be selected. Only runs in <strong>Queued Work Ready</strong> status are available for selection.</span>
              </div>
            )}
            <div className='table-responsive ppq-table-wrapper'>
              <table className='ppq-table'>
                <thead>
                  <tr>
                    <th>
                      <Form.Check
                        type='checkbox'
                        checked={selectableRuns.length > 0 && selectedRunUris.length > 0 && selectedRunUris.length === Math.min(selectableRuns.length, remainingCapacity)}
                        className='ppq-select-all'
                        disabled={runs.length === 0 || (selectedRunUris.length === 0 && remainingCapacity === 0)}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Pos.</th>
                    <th>Run name</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Scheduled start</th>
                    <th>Scheduled end</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run, index) => renderRunRow(run, index, true))}
                  {runs.length === 0 && (
                    <tr>
                      <td colSpan='7' className='text-center p-4'>No processing runs are currently assigned to this post processor.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className='ppq-mobile-cards'>
              {runs.map((run, index) => renderMobileCard(run, index, true))}
              {runs.length === 0 && (
                <div className='ppq-mobile-empty'>No upcoming runs</div>
              )}
            </div>
            <div className='ppq-queue-footer'>
              <div className='ppq-footer-counts'>
                <div className='ppq-selected-count'>
                  {selectedRunUris.length} run{selectedRunUris.length === 1 ? '' : 's'} selected
                </div>
                <div className='ppq-queue-count'>
                  Showing {runs.length} of {totalRuns}
                </div>
              </div>

              <div className='ppq-footer-actions'>
                <Button
                  variant='primary'
                  size='sm'
                  disabled={selectedRunUris.length === 0 || startingRuns || selectedRunUris.length > remainingCapacity}
                  onClick={handleStartRuns}
                >
                  {startingRuns ?
                    <Loader inline /> :
                    <><FontAwesomeIcon icon={faPlay} className='mr-1' />Start selected runs</>}
                </Button>
                {runs.length < totalRuns && (
                  <Button variant='outline-primary' size='sm' disabled={loadingMore} onClick={handleLoadMore}>
                    {loadingMore ?
                      <Loader inline /> :
                      <><FontAwesomeIcon icon={faChevronDown} className='mr-1' />Load more runs</>}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {isAiScadaIntegration && (
            <div className='ppq-load-carriers-card'>
              <div className='ppq-load-carriers-info'>
                <div className='ppq-load-carriers-title'>Load carriers</div>
                <div className='ppq-load-carriers-subtitle'>
                  Request additional load carriers for this station
                </div>
              </div>
              <Button variant='primary' size='sm' disabled={isOrderedLoadCarries} onClick={handleOrderLoadCarriers}>
                <FontAwesomeIcon icon={faCube} className='mr-1' />
                Order more load carriers
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

PostProcessorQueuePage.propTypes = {
  user: userPropType,
};

PostProcessorQueuePage.defaultProps = {
  user: null,
};

export default PostProcessorQueuePage;
